import React, { useState, useEffect } from "react";
import {
	Box,
	Typography,
	TextField,
	IconButton,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Chip,
	Tabs,
	Tab,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CustomAutocomplete from "../../../components/CustomAutocomplete";
import { UnidadeMedidaService } from "../../../service/unidade_medida_service";
import { CultivoService } from "../../../service/cultivo_service";

function AbaProdutos({ produtos, setProdutos, onAddProdutoClick }) {
	const [openDialog, setOpenDialog] = useState(false);
	const [produtoAtual, setProdutoAtual] = useState(null);
	const [cultivoSelecionado, setCultivoSelecionado] = useState(null);
	const [codigoBarras, setCodigoBarras] = useState("");
	const [embalagens, setEmbalagens] = useState([]);
	const [cultivos, setCultivos] = useState([
		{ id: 1, nome: "Carregando..." }
	]);
	const [unidadesMedida, setUnidadesMedida] = useState([]);
	const [abaAtiva, setAbaAtiva] = useState("entrada");

	// Estados para o formulário de nova embalagem
	const [novaEmbalagem, setNovaEmbalagem] = useState({
		descricao: "",
		quantidade: "",
		unidadeMedida: null,
	});
	const [editandoEmbalagemId, setEditandoEmbalagemId] = useState(null);

	// Expor a função handleAddProduto para o componente pai
	useEffect(() => {
		if (onAddProdutoClick) {
			onAddProdutoClick(() => handleAddProduto);
		}
	}, [onAddProdutoClick]);

	useEffect(() => {
		// Buscar cultivos do backend
		const loadCultivos = async () => {
			try {
				const cultivoService = new CultivoService();
				const cultivationParams = {
					page: 1,
					limit: 100000,
					sortBy: JSON.stringify([
						{ cultura: "asc" },
						{ variedade: "asc" }
					])
				};
				const response = await cultivoService.getAll(cultivationParams);
				// A API retorna um objeto com cultivations contendo os cultivos
				const cultivosArray = response?.cultivations || [];
				setCultivos(cultivosArray);
			} catch (error) {
				console.error("Erro ao carregar cultivos:", error);
				// Fallback para dados mock em caso de erro
				setCultivos([
					{ id: 1, nome: "Café Arábica" },
					{ id: 2, nome: "Café Robusta" },
					{ id: 3, nome: "Milho" },
					{ id: 4, nome: "Soja" },
				]);
			}
		};

		// Buscar unidades de medida do backend
		const loadUnidadesMedida = async () => {
			try {
				const unidadeMedidaService = new UnidadeMedidaService();
				const data = await unidadeMedidaService.getByCultivo();
				setUnidadesMedida(data);
			} catch (error) {
				console.error("Erro ao carregar unidades de medida:", error);
				// Fallback para dados mock em caso de erro
				setUnidadesMedida([
					{ id: 1, sigla: "kg" },
					{ id: 2, sigla: "g" },
					{ id: 3, sigla: "L" },
					{ id: 4, sigla: "mL" },
					{ id: 5, sigla: "un" },
				]);
			}
		};

		loadCultivos();
		loadUnidadesMedida();
	}, []);

	const handleAddProduto = () => {
		setProdutoAtual(null);
		// Usar abaAtiva diretamente ao salvar, não armazenar no estado
		setCultivoSelecionado(null);
		setCodigoBarras("");
		setEmbalagens([]);
		setNovaEmbalagem({ descricao: "", quantidade: "", unidadeMedida: null });
		setEditandoEmbalagemId(null);
		setOpenDialog(true);
	};

	const handleOpenDialog = (produto) => {
		setProdutoAtual(produto);
		setCultivoSelecionado(produto.cultivo);
		setCodigoBarras(produto.codigoBarras || "");
		setEmbalagens(produto.embalagens || []);
		setNovaEmbalagem({ descricao: "", quantidade: "", unidadeMedida: null });
		setEditandoEmbalagemId(null);
		setOpenDialog(true);
	};

	const handleCloseDialog = () => {
		setOpenDialog(false);
		setProdutoAtual(null);
		setCultivoSelecionado(null);
		setCodigoBarras("");
		setEmbalagens([]);
		setNovaEmbalagem({ descricao: "", quantidade: "", unidadeMedida: null });
		setEditandoEmbalagemId(null);
	};

	const handleAddOuEditarEmbalagem = () => {
		if (!novaEmbalagem.descricao || !novaEmbalagem.quantidade || !novaEmbalagem.unidadeMedida) {
			alert("Preencha todos os campos da embalagem");
			return;
		}

		if (editandoEmbalagemId) {
			// Editando embalagem existente
			setEmbalagens(
				embalagens.map((emb) =>
					emb.id === editandoEmbalagemId
						? {
								...emb,
								descricao: novaEmbalagem.descricao,
								quantidade: novaEmbalagem.quantidade,
								unidadeMedida: novaEmbalagem.unidadeMedida,
						  }
						: emb
				)
			);
			setEditandoEmbalagemId(null);
		} else {
			// Adicionando nova embalagem
			const embalagem = {
				id: Date.now(),
				descricao: novaEmbalagem.descricao,
				quantidade: novaEmbalagem.quantidade,
				unidadeMedida: novaEmbalagem.unidadeMedida,
			};
			setEmbalagens([...embalagens, embalagem]);
		}

		// Limpar formulário
		setNovaEmbalagem({ descricao: "", quantidade: "", unidadeMedida: null });
	};

	const handleEditarEmbalagem = (embalagem) => {
		setNovaEmbalagem({
			descricao: embalagem.descricao,
			quantidade: embalagem.quantidade,
			unidadeMedida: embalagem.unidadeMedida,
		});
		setEditandoEmbalagemId(embalagem.id);
	};

	const handleCancelarEdicao = () => {
		setNovaEmbalagem({ descricao: "", quantidade: "", unidadeMedida: null });
		setEditandoEmbalagemId(null);
	};

	const handleRemoveEmbalagem = (id) => {
		setEmbalagens(embalagens.filter((emb) => emb.id !== id));
		// Se estava editando essa embalagem, cancelar edição
		if (editandoEmbalagemId === id) {
			handleCancelarEdicao();
		}
	};

	const handleSaveProduto = () => {
		if (!cultivoSelecionado || embalagens.length === 0) {
			alert("Selecione um cultivo e adicione pelo menos uma embalagem");
			return;
		}

		// Usar abaAtiva ao salvar, que é o valor atual da aba
		const tipoFinal = produtoAtual ? produtoAtual.tipo : abaAtiva;

		const novoProduto = {
			id: produtoAtual?.id || Date.now(),
			tipo: tipoFinal,
			cultivo: cultivoSelecionado,
			codigoBarras: codigoBarras,
			embalagens: embalagens,
		};

		if (produtoAtual) {
			// Editar produto existente
			setProdutos(
				produtos.map((p) => (p.id === produtoAtual.id ? novoProduto : p))
			);
		} else {
			// Adicionar novo produto
			setProdutos([...produtos, novoProduto]);
		}

		handleCloseDialog();
	};

	const handleDeleteProduto = (id) => {
		if (window.confirm("Deseja realmente excluir este produto?")) {
			setProdutos(produtos.filter((p) => p.id !== id));
		}
	};

	const sortByName = (a, b) => {
		const nomeA = `${a.cultivo?.cultura || ""} ${a.cultivo?.variedade || ""}`.trim().toLowerCase();
		const nomeB = `${b.cultivo?.cultura || ""} ${b.cultivo?.variedade || ""}`.trim().toLowerCase();
		return nomeA.localeCompare(nomeB, "pt-BR");
	};

	const produtosEntrada = produtos.filter((p) => p.tipo === "entrada").sort(sortByName);
	const produtosFinais = produtos.filter((p) => p.tipo === "final").sort(sortByName);

	const renderTabelaProdutos = (listaProdutos) => {
		if (listaProdutos.length === 0) {
			return (
				<Box
					sx={{
						textAlign: "center",
						py: 6,
						px: 8,
						color: "#999",
						border: "2px dashed #e0e0e0",
						borderRadius: 1,
						width: "100%",
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						minHeight: "120px",
					}}
				>
					<Typography variant="body1" sx={{ fontSize: "16px", fontWeight: 500 }}>
						Nenhum produto adicionado
					</Typography>
				</Box>
			);
		}

		return (
			<TableContainer>
				<Table sx={{ tableLayout: "fixed" }}>
					<TableHead>
						<TableRow>
							<TableCell sx={{ color: "#1a9e8f", fontWeight: 600, fontSize: "15px", borderBottom: "1px solid #e0e0e0", pl: 0, pr: 2 }}>
								Cultivo
							</TableCell>
							<TableCell sx={{ color: "#1a9e8f", fontWeight: 600, fontSize: "15px", borderBottom: "1px solid #e0e0e0", px: 2, width: "25%" }}>
								Código de Barras
							</TableCell>
							<TableCell sx={{ color: "#1a9e8f", fontWeight: 600, fontSize: "15px", borderBottom: "1px solid #e0e0e0", px: 2 }}>
								Embalagens
							</TableCell>
							<TableCell sx={{ color: "#1a9e8f", fontWeight: 600, fontSize: "15px", borderBottom: "1px solid #e0e0e0", pl: 2, pr: 0, width: "150px" }} align="center">
								Ações
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{listaProdutos.map((produto) => (
							<TableRow key={produto.id} sx={{ "&:hover": { bgcolor: "#f9f9f9" } }}>
								<TableCell sx={{ fontSize: "15px", borderBottom: "1px solid #f0f0f0", pl: 0, pr: 2 }}>
									{`${produto.cultivo.cultura || ""} ${produto.cultivo.variedade || ""}`.trim()}
								</TableCell>
								<TableCell sx={{ fontSize: "15px", borderBottom: "1px solid #f0f0f0", px: 2 }}>
									{produto.codigoBarras || "-"}
								</TableCell>
								<TableCell sx={{ fontSize: "15px", borderBottom: "1px solid #f0f0f0", px: 2 }}>
									<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
										{produto.embalagens.map((emb) => (
											<Chip
												key={emb.id}
												label={`${emb.descricao} - ${emb.quantidade} ${emb.unidadeMedida?.sigla || emb.unidadeMedida}`}
												size="small"
												sx={{
													bgcolor: "#E7F6F8",
													color: "#1a9e8f",
													fontSize: "12px",
												}}
											/>
										))}
									</Box>
								</TableCell>
								<TableCell sx={{ fontSize: "15px", borderBottom: "1px solid #f0f0f0", pl: 2, pr: 0 }} align="center">
									<IconButton
										size="small"
										sx={{ color: "#1a9e8f" }}
										onClick={() => handleOpenDialog(produto)}
										title="Editar produto"
									>
										<EditIcon fontSize="small" />
									</IconButton>
									<IconButton
										size="small"
										onClick={() => handleDeleteProduto(produto.id)}
										sx={{ color: "#ff4d4d", ml: 0.5 }}
										title="Excluir produto"
									>
										<DeleteIcon fontSize="small" />
									</IconButton>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>
		);
	};

	return (
		<Box>
			{/* Abas de Produto de Entrada e Produto Final */}
			<Tabs
				value={abaAtiva}
				onChange={(e, newValue) => setAbaAtiva(newValue)}
				sx={{
					mb: 3,
					"& .MuiTab-root": {
						textTransform: "none",
						fontSize: "15px",
						fontWeight: 500,
						color: "#1a9e8f",
						minHeight: "42px",
					},
					"& .Mui-selected": {
						color: "#1a9e8f",
					},
					"& .MuiTabs-indicator": {
						backgroundColor: "#1a9e8f",
					},
				}}
			>
				<Tab label="Produtos de Entrada" value="entrada" />
				<Tab label="Produtos Finais" value="final" />
			</Tabs>

			{/* Conteúdo das abas */}
			{abaAtiva === "entrada" && (
				<Box>
					<Typography
						variant="subtitle1"
						sx={{ color: "#1a9e8f", fontWeight: 600, fontSize: "16px", mb: 2 }}
					>
						Produtos de Entrada
					</Typography>
					{renderTabelaProdutos(produtosEntrada)}
				</Box>
			)}

			{abaAtiva === "final" && (
				<Box>
					<Typography
						variant="subtitle1"
						sx={{ color: "#1a9e8f", fontWeight: 600, fontSize: "16px", mb: 2 }}
					>
						Produtos Finais
					</Typography>
					{renderTabelaProdutos(produtosFinais)}
				</Box>
			)}

			{/* Dialog para adicionar/editar produto */}
			<Dialog
				open={openDialog}
				onClose={handleCloseDialog}
				maxWidth="md"
				fullWidth
			>
				<DialogTitle sx={{ bgcolor: "#1a9e8f", color: "white", fontSize: "18px", fontWeight: 600 }}>
					{produtoAtual ? "Editar Produto" : "Adicionar Produto"}
				</DialogTitle>
				<DialogContent sx={{ mt: 3, px: 3 }}>
					<Box sx={{ display: "flex", flexDirection: "column", gap: 3, width: "100%" }}>
						{/* BLOCO 1: Produto */}
						<Box sx={{ width: "100%" }}>
							<Typography variant="subtitle1" sx={{ color: "#1a9e8f", fontWeight: 600, fontSize: "16px", mb: 2 }}>
								Produto
							</Typography>
							<Box sx={{ display: "flex", gap: 2, width: "100%" }}>
								<Box sx={{ flex: 3 }}>
									<CustomAutocomplete
										value={cultivoSelecionado}
										onChange={(newValue) => setCultivoSelecionado(newValue)}
										options={cultivos}
										getOptionLabel={(option) => `${option.cultura || ""} ${option.variedade || ""}`.trim()}
										getOptionId={(option) => option.id}
										label="Produto"
										required
										placeholder="Buscar produto..."
										noOptionsText="Nenhum produto encontrado"
									/>
								</Box>
								<Box sx={{ flex: 1 }}>
									<TextField
										fullWidth
										label="Código de Barras"
										value={codigoBarras}
										onChange={(e) => setCodigoBarras(e.target.value)}
										InputLabelProps={{ style: { fontSize: "15px" } }}
										inputProps={{ style: { fontSize: "15px" } }}
									/>
								</Box>
							</Box>
						</Box>

						{/* BLOCO 2: Formulário de Embalagens */}
						<Box sx={{ width: "100%" }}>
							<Typography variant="subtitle1" sx={{ color: "#1a9e8f", fontWeight: 600, fontSize: "16px", mb: 2 }}>
								Embalagens
							</Typography>
							<Box sx={{ display: "flex", gap: 2, width: "100%", alignItems: "flex-end" }}>
								<Box sx={{ flex: 2 }}>
									<TextField
										fullWidth
										label="Embalagem"
										value={novaEmbalagem.descricao}
										onChange={(e) => setNovaEmbalagem({ ...novaEmbalagem, descricao: e.target.value })}
										InputLabelProps={{ style: { fontSize: "15px" } }}
										inputProps={{ style: { fontSize: "15px" } }}
									/>
								</Box>
								<Box sx={{ flex: 1 }}>
									<TextField
										fullWidth
										label="Quantidade"
										type="number"
										value={novaEmbalagem.quantidade}
										onChange={(e) => setNovaEmbalagem({ ...novaEmbalagem, quantidade: e.target.value })}
										InputLabelProps={{ style: { fontSize: "15px" } }}
										inputProps={{ style: { fontSize: "15px" } }}
									/>
								</Box>
								<Box sx={{ flex: 2 }}>
									<CustomAutocomplete
										value={novaEmbalagem.unidadeMedida}
										onChange={(newValue) => setNovaEmbalagem({ ...novaEmbalagem, unidadeMedida: newValue })}
										options={unidadesMedida}
										getOptionLabel={(option) => option.sigla || ""}
										getOptionId={(option) => option.id}
										label="Unidade de Medida"
										placeholder="Selecionar..."
									/>
								</Box>
								<Box sx={{ flex: 1 }}>
									{editandoEmbalagemId ? (
										<Box sx={{ display: "flex", gap: 0.5 }}>
											<Button
												variant="contained"
												onClick={handleAddOuEditarEmbalagem}
												fullWidth
												sx={{
													bgcolor: "#1a9e8f",
													"&:hover": { bgcolor: "#138a7d" },
													textTransform: "none",
													fontSize: "14px",
													height: "56px",
												}}
											>
												Salvar
											</Button>
											<IconButton
												onClick={handleCancelarEdicao}
												sx={{ color: "#666", height: "56px" }}
											>
												<DeleteIcon />
											</IconButton>
										</Box>
									) : (
										<Button
											variant="contained"
											onClick={handleAddOuEditarEmbalagem}
											fullWidth
											sx={{
												bgcolor: "#1a9e8f",
												"&:hover": { bgcolor: "#138a7d" },
												textTransform: "none",
												fontSize: "15px",
												height: "56px",
											}}
										>
											Adicionar
										</Button>
									)}
								</Box>
							</Box>
						</Box>

						{/* BLOCO 3: Tabela de Embalagens */}
						<Box>
							<TableContainer>
								<Table>
									<TableHead>
										<TableRow>
											<TableCell sx={{ color: "#1a9e8f", fontWeight: 600, fontSize: "15px", borderBottom: "1px solid #e0e0e0" }}>
												Embalagem
											</TableCell>
											<TableCell sx={{ color: "#1a9e8f", fontWeight: 600, fontSize: "15px", borderBottom: "1px solid #e0e0e0", width: "20%" }}>
												Quantidade
											</TableCell>
											<TableCell sx={{ color: "#1a9e8f", fontWeight: 600, fontSize: "15px", borderBottom: "1px solid #e0e0e0", width: "25%" }}>
												Unidade
											</TableCell>
											<TableCell sx={{ color: "#1a9e8f", fontWeight: 600, fontSize: "15px", borderBottom: "1px solid #e0e0e0", width: "120px" }} align="center">
												Ações
											</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{embalagens.length === 0 ? (
											<TableRow>
												<TableCell colSpan={4} sx={{ textAlign: "center", py: 3, color: "#999", borderBottom: "none" }}>
													<Typography variant="body2" sx={{ fontSize: "15px" }}>
														Nenhuma embalagem adicionada
													</Typography>
												</TableCell>
											</TableRow>
										) : (
											embalagens.map((emb) => (
												<TableRow key={emb.id} sx={{ "&:hover": { bgcolor: "#f9f9f9" } }}>
													<TableCell sx={{ fontSize: "15px", borderBottom: "1px solid #f0f0f0" }}>
														{emb.descricao}
													</TableCell>
													<TableCell sx={{ fontSize: "15px", borderBottom: "1px solid #f0f0f0" }}>
														{emb.quantidade}
													</TableCell>
													<TableCell sx={{ fontSize: "15px", borderBottom: "1px solid #f0f0f0" }}>
														{emb.unidadeMedida?.sigla || "-"}
													</TableCell>
													<TableCell sx={{ fontSize: "15px", borderBottom: "1px solid #f0f0f0" }} align="center">
														<IconButton
															size="small"
															sx={{ color: "#1a9e8f" }}
															onClick={() => handleEditarEmbalagem(emb)}
															title="Editar embalagem"
														>
															<EditIcon fontSize="small" />
														</IconButton>
														<IconButton
															size="small"
															onClick={() => handleRemoveEmbalagem(emb.id)}
															sx={{ color: "#ff4d4d", ml: 0.5 }}
															title="Excluir embalagem"
														>
															<DeleteIcon fontSize="small" />
														</IconButton>
													</TableCell>
												</TableRow>
											))
										)}
									</TableBody>
								</Table>
							</TableContainer>
						</Box>
					</Box>
				</DialogContent>
				<DialogActions sx={{ p: 2 }}>
					<Button onClick={handleCloseDialog} sx={{ color: "#666", textTransform: "none", fontSize: "15px" }}>
						Cancelar
					</Button>
					<Button
						onClick={handleSaveProduto}
						variant="contained"
						sx={{
							bgcolor: "#1a9e8f",
							"&:hover": {
								bgcolor: "#138a7d",
							},
							textTransform: "none",
							fontSize: "15px",
						}}
					>
						Salvar
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
}

export default AbaProdutos;
